import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Package,
  Truck,
  CheckCircle,
  Clock,
  Shirt,
  Home as HomeIcon,
  ChevronRight,
  RefreshCw,
  ArrowLeft as ArrowLeftIcon,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { fetchSolicitudesPendientes } from '@/services/solicitudes.service';
import { 
  fetchEntregasAlistadas,
  fetchEntregasEnCamino,
  fetchEntregasCompletadas,
  fetchEntregasRecolectadas,
  marcarEntregasEnCamino,
  marcarEntregaEnCamino 
} from '@/services/entregas.service';
import { completarEntrega } from '@/services/completarEntrega.service';

// Componentes
import SolicitudesPendientes from './components/SolicitudesPendientes';
import EntregasAlistadas from './components/EntregasAlistadas';
import EntregasEnCamino from './components/EntregasEnCamino';
import EntregasCompletadas from './components/EntregasCompletadas';
import EntregasRecolectadas from './components/EntregasRecolectadas';
import RecoleccionRopa from './components/RecoleccionRopa';

const RutaPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  // Tab activo ('home' = dashboard de tarjetas, el resto = vista de tab)
  const [activeTab, setActiveTab] = useState('home');

  // Estados de datos
  const [solicitudesPendientes, setSolicitudesPendientes] = useState([]);
  const [entregasAlistadas, setEntregasAlistadas] = useState([]);
  const [entregasEnCamino, setEntregasEnCamino] = useState([]);
  const [entregasCompletadas, setEntregasCompletadas] = useState([]);
  const [entregasRecolectadas, setEntregasRecolectadas] = useState([]);

  // Estados de carga
  const [isLoading, setIsLoading] = useState(false);

  // Contadores
  const [counters, setCounters] = useState({
    pendientes: 0,
    alistadas: 0,
    enCamino: 0,
    completadas: 0,
    recolectadas: 0,
  });

  // Cargar datos iniciales
  useEffect(() => {
    loadData();
  }, []);

  // Recargar datos cuando cambia el tab (en 'home' no recarga: usa contadores ya cargados)
  useEffect(() => {
    if (activeTab === 'home') return;

    if (activeTab === 'pendientes') {
      loadSolicitudesPendientes();
    } else if (activeTab === 'alistadas') {
      loadEntregasAlistadas();
    } else if (activeTab === 'en-camino') {
      loadEntregasEnCamino();
    } else if (activeTab === 'completadas') {
      loadEntregasCompletadas();
    } else if (activeTab === 'recolectadas') {
      loadEntregasRecolectadas();
    }
  }, [activeTab]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        loadSolicitudesPendientes(),
        loadEntregasAlistadas(),
        loadEntregasEnCamino(),
        loadEntregasCompletadas(),
        loadEntregasRecolectadas(),
      ]);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadSolicitudesPendientes = async () => {
    try {
      const response = await fetchSolicitudesPendientes();
      
      if (response && response.solicitudes) {
        setSolicitudesPendientes(response.solicitudes);
        setCounters(prev => ({ 
          ...prev, 
          pendientes: response.total || 0 
        }));
      }
    } catch (error) {
      console.error('Error al cargar solicitudes pendientes:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las solicitudes pendientes',
        variant: 'destructive',
      });
      setSolicitudesPendientes([]);
      setCounters(prev => ({ ...prev, pendientes: 0 }));
    }
  };

  const loadEntregasAlistadas = async () => {
    try {
      const response = await fetchEntregasAlistadas();
      
      if (response && response.entregas) {
        setEntregasAlistadas(response.entregas);
        setCounters(prev => ({ 
          ...prev, 
          alistadas: response.total || 0 
        }));
      }
    } catch (error) {
      console.error('Error al cargar entregas alistadas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las entregas alistadas',
        variant: 'destructive',
      });
      setEntregasAlistadas([]);
      setCounters(prev => ({ ...prev, alistadas: 0 }));
    }
  };

  const loadEntregasEnCamino = async () => {
    try {
      const response = await fetchEntregasEnCamino();
      
      setEntregasEnCamino(response.entregas || []);
      setCounters(prev => ({ 
        ...prev, 
        enCamino: response.total || 0 
      }));
    } catch (error) {
      console.error('Error al cargar entregas en camino:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las entregas en camino',
        variant: 'destructive',
      });
      setEntregasEnCamino([]);
      setCounters(prev => ({ ...prev, enCamino: 0 }));
    }
  };

  const loadEntregasCompletadas = async () => {
    try {
      const response = await fetchEntregasCompletadas();
      
      setEntregasCompletadas(response.entregas || []);
      setCounters(prev => ({ 
        ...prev, 
        completadas: response.total || 0 
      }));
    } catch (error) {
      console.error('Error al cargar entregas completadas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las entregas completadas',
        variant: 'destructive',
      });
      setEntregasCompletadas([]);
      setCounters(prev => ({ ...prev, completadas: 0 }));
    }
  };

  const loadEntregasRecolectadas = async () => {
    try {
      const response = await fetchEntregasRecolectadas();
      
      setEntregasRecolectadas(response.entregas || []);
      setCounters(prev => ({ 
        ...prev, 
        recolectadas: response.total || 0 
      }));
    } catch (error) {
      console.error('Error al cargar entregas recolectadas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las entregas recolectadas',
        variant: 'destructive',
      });
      setEntregasRecolectadas([]);
      setCounters(prev => ({ ...prev, recolectadas: 0 }));
    }
  };

  // Handlers
  const handleMarcarEnCamino = async (entregasSeleccionadas) => {
    if (!entregasSeleccionadas || entregasSeleccionadas.length === 0) {
      toast({
        title: 'Advertencia',
        description: 'Debe seleccionar al menos una entrega',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      const conductor = {
        id: user._id,
        nombre: user.name || user.nombre,
      };

      const result = await marcarEntregasEnCamino(entregasSeleccionadas, conductor);

      if (result.success) {
        toast({
          title: 'Éxito',
          description: `${result.totalProcesadas} entrega(s) marcada(s) en camino`,
        });

        // Recargar datos
        await Promise.all([
          loadEntregasAlistadas(),
          loadEntregasEnCamino(),
        ]);
      } else {
        toast({
          title: 'Advertencia',
          description: `${result.totalProcesadas} exitosas, ${result.totalErrores} con errores`,
          variant: 'destructive',
        });

        // Recargar de todas formas
        await Promise.all([
          loadEntregasAlistadas(),
          loadEntregasEnCamino(),
        ]);
      }
    } catch (error) {
      console.error('Error al marcar entregas en camino:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron marcar las entregas en camino',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompletarEntrega = async (datosEntrega) => {
    try {
      const result = await completarEntrega(datosEntrega);

      toast({
        title: 'Éxito',
        description: 'Entrega completada correctamente',
      });

      // Recargar entregas
      await Promise.all([
        loadEntregasEnCamino(),
        loadEntregasCompletadas(),
      ]);

      return result;
    } catch (error) {
      console.error('Error al completar entrega:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo completar la entrega',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Tabs para mobile (incluye 'home' como dashboard inicial)
  const tabs = [
    {
      id: 'home',
      label: 'Inicio',
      shortLabel: 'Inicio',
      icon: HomeIcon,
      count: 0,
      color: 'text-slate-700',
      // Campos solo usados en el dashboard (no en la barra)
      dashboardTitle: 'Inicio',
      dashboardDescription: 'Resumen general de tu ruta',
      isHome: true,
    },
    {
      id: 'pendientes',
      label: 'Pendientes',
      shortLabel: 'Pendientes',
      icon: Clock,
      count: counters.pendientes,
      color: 'text-orange-600',
      dashboardTitle: 'Solicitudes Pendientes',
      dashboardDescription: 'Solicitudes que aún no se han procesado',
      bgClass: 'bg-orange-50',
      borderClass: 'border-orange-200',
      iconBgClass: 'bg-orange-100',
    },
    {
      id: 'alistadas',
      label: 'Alistadas',
      shortLabel: 'Alistadas',
      icon: Package,
      count: counters.alistadas,
      color: 'text-blue-600',
      dashboardTitle: 'Entregas Alistadas',
      dashboardDescription: 'Listas para salir a ruta',
      bgClass: 'bg-blue-50',
      borderClass: 'border-blue-200',
      iconBgClass: 'bg-blue-100',
    },
    {
      id: 'en-camino',
      label: 'En Camino',
      shortLabel: 'En Camino',
      icon: Truck,
      count: counters.enCamino,
      color: 'text-yellow-600',
      dashboardTitle: 'Entregas en Camino',
      dashboardDescription: 'En ruta hacia su destino',
      bgClass: 'bg-yellow-50',
      borderClass: 'border-yellow-200',
      iconBgClass: 'bg-yellow-100',
    },
    {
      id: 'completadas',
      label: 'Entregadas',
      shortLabel: 'Entregadas',
      icon: CheckCircle,
      count: counters.completadas,
      color: 'text-green-600',
      dashboardTitle: 'Entregas Completadas',
      dashboardDescription: 'Entregas finalizadas con éxito',
      bgClass: 'bg-green-50',
      borderClass: 'border-green-200',
      iconBgClass: 'bg-green-100',
    },
    {
      id: 'recoleccion',
      label: 'Recolección',
      shortLabel: 'Recolección',
      icon: Shirt,
      count: 0,
      color: 'text-purple-600',
      dashboardTitle: 'Recolección de Ropa',
      dashboardDescription: 'Recoge ropa sucia de los hospedajes',
      bgClass: 'bg-purple-50',
      borderClass: 'border-purple-200',
      iconBgClass: 'bg-purple-100',
    },
    {
      id: 'recolectadas',
      label: 'Recolectadas',
      shortLabel: 'Recolectadas',
      icon: Package,
      count: counters.recolectadas,
      color: 'text-purple-600',
      dashboardTitle: 'Ropa Recolectada',
      dashboardDescription: 'Recolecciones ya finalizadas',
      bgClass: 'bg-purple-50',
      borderClass: 'border-purple-200',
      iconBgClass: 'bg-purple-100',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header - Fixed */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="flex items-center justify-between px-4 bg-white">
          {activeTab !== 'home' ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActiveTab('home')}
              className="gap-2"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Volver
            </Button>
          ) : (
            <span />
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (activeTab === 'home') {
                loadData();
              } else {
                if (activeTab === 'pendientes') loadSolicitudesPendientes();
                else if (activeTab === 'alistadas') loadEntregasAlistadas();
                else if (activeTab === 'en-camino') loadEntregasEnCamino();
                else if (activeTab === 'completadas') loadEntregasCompletadas();
                else if (activeTab === 'recolectadas') loadEntregasRecolectadas();
              }
            }}
            disabled={isLoading}
            className="gap-1"
            title="Actualizar"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Content Area */}
      <div className="p-2">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'home' && (
            <DashboardHome
              tabs={tabs}
              counters={counters}
              onSelectTab={setActiveTab}
              isLoading={isLoading}
            />
          )}

          {activeTab === 'recoleccion' && (
            <RecoleccionRopa
              onRecoleccionExitosa={() => {
                loadEntregasRecolectadas();
                setActiveTab('recolectadas');
              }}
            />
          )}

          {activeTab === 'recolectadas' && (
            <EntregasRecolectadas
              entregas={entregasRecolectadas}
              isLoading={isLoading}
              onRefresh={loadEntregasRecolectadas}
            />
          )}

          {activeTab === 'pendientes' && (
            <SolicitudesPendientes
              solicitudes={solicitudesPendientes}
              isLoading={isLoading}
              onRefresh={loadSolicitudesPendientes}
            />
          )}

          {activeTab === 'alistadas' && (
            <EntregasAlistadas
              entregas={entregasAlistadas}
              isLoading={isLoading}
              onMarcarEnCamino={handleMarcarEnCamino}
              onRefresh={loadEntregasAlistadas}
            />
          )}

          {activeTab === 'en-camino' && (
            <EntregasEnCamino
              entregas={entregasEnCamino}
              isLoading={isLoading}
              onCompletar={handleCompletarEntrega}
              onRefresh={loadEntregasEnCamino}
            />
          )}

          {activeTab === 'completadas' && (
            <EntregasCompletadas
              entregas={entregasCompletadas}
              isLoading={isLoading}
              onRefresh={loadEntregasCompletadas}
            />
          )}
        </motion.div>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <Card className="p-6">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              <p className="text-sm text-gray-600">Cargando...</p>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// Dashboard de tarjetas: vista principal mobile-friendly
// Muestra cada categoría como una tarjeta grande con icono, conteo, descripción y CTA.
const DashboardHome = ({ tabs, counters, onSelectTab, isLoading }) => {
  const totalPendientes =
    counters.pendientes +
    counters.alistadas +
    counters.enCamino;

  const totalCompletadas =
    counters.completadas +
    counters.recolectadas;

  const dashboardTabs = tabs.filter(t => t.id !== 'home' && !t.isHome);

  return (
    <div className="space-y-4">
      {/* Encabezado de bienvenida */}
{/*       <div className="rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 shadow-sm">
        <h2 className="text-lg font-bold">Tu ruta, de un vistazo</h2>
        <p className="text-sm text-blue-100 mt-1">
          Toca una tarjeta para ver el detalle de cada sección.
        </p>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="bg-white/10 rounded-md p-3 backdrop-blur">
            <p className="text-xs text-blue-100">Pendientes de ruta</p>
            <p className="text-2xl font-bold mt-1">{totalPendientes}</p>
          </div>
          <div className="bg-white/10 rounded-md p-3 backdrop-blur">
            <p className="text-xs text-blue-100">Completadas hoy</p>
            <p className="text-2xl font-bold mt-1">{totalCompletadas}</p>
          </div>
        </div>
      </div> */}

      {/* Grid de tarjetas (1 columna en móvil, 2 en tablet+) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {dashboardTabs.map((tab) => {
          const Icon = tab.icon;
          const isEmpty = !isLoading && (!tab.count || tab.count === 0);

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onSelectTab(tab.id)}
              className={`
                group text-left rounded-xl border-2 p-4 transition-all
                active:scale-[0.98] hover:shadow-md focus:outline-none
                focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                ${tab.bgClass || 'bg-white'}
                ${tab.borderClass || 'border-gray-200'}
                ${isEmpty ? 'opacity-70' : ''}
              `}
            >
              <div className="flex items-start justify-between gap-3">
                <div
                  className={`
                    flex items-center justify-center w-12 h-12 rounded-full
                    ${tab.iconBgClass || 'bg-gray-100'}
                  `}
                >
                  <Icon className={`h-6 w-6 ${tab.color}`} />
                </div>

                {tab.count > 0 && (
                  <Badge variant="default" className="text-sm px-2 py-0.5">
                    {tab.count}
                  </Badge>
                )}
              </div>

              <h3 className="mt-3 text-base font-semibold text-gray-900">
                {tab.dashboardTitle}
              </h3>
              <p className="mt-1 text-sm text-gray-600 leading-snug">
                {tab.dashboardDescription}
              </p>

              <div className="mt-3 flex items-center justify-between">
                <span
                  className={`
                    text-sm font-medium
                    ${tab.color}
                  `}
                >
                  {isEmpty ? 'Sin pendientes' : 'Ver detalle'}
                </span>
                <ChevronRight
                  className={`h-5 w-5 ${tab.color} transition-transform group-hover:translate-x-1`}
                />
              </div>
            </button>
          );
        })}
      </div>

      {isLoading && (
        <p className="text-center text-sm text-gray-500 py-2">
          Actualizando contadores...
        </p>
      )}
    </div>
  );
};

export default RutaPage;
